import { SpinBoundingBox } from '|/services/vex-spinner';
import { CropBox, SourceDimensions } from './types';

export const DEFAULT_CROP_PAD = 0.06;

const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

const clampUnit = (value: number) => Math.min(Math.max(value, 0), 1);

/** `images.dimensions` is a `"9504x6336"` string, and it varies per vehicle (8256x5504 also
 *  occurs), so the source size has to be parsed rather than assumed. */
export const parseSourceDimensions = (dimensions?: string | null): SourceDimensions | null => {
  const match = /^(\d+)x(\d+)$/.exec(dimensions?.trim() ?? '');

  if (!match) return null;

  const width = Number(match[1]);
  const height = Number(match[2]);

  return width > 0 && height > 0 ? { width, height } : null;
};

interface CropInput {
  boundingBox?: SpinBoundingBox | null;
  source?: SourceDimensions | null;
  aspect: number;
  pad?: number;
}

/**
 * Fastly's own `crop=W:H` centre-crops the dome frame and clips the roof and tyres, hence the
 * explicit box. Takes `spins[].boundingBox` (the car), never `cleanedFloor.boundingBox3by2`
 * (an already-computed 3:2 box) — the two look interchangeable and are not.
 */
export const computeCropBox = ({
  boundingBox,
  source,
  aspect,
  pad = DEFAULT_CROP_PAD
}: CropInput): CropBox | null => {
  if (!boundingBox || !source) return null;
  if (!isFiniteNumber(aspect) || aspect <= 0) return null;

  const { width: sourceWidth, height: sourceHeight } = source;

  if (!isFiniteNumber(sourceWidth) || !isFiniteNumber(sourceHeight)) return null;
  if (sourceWidth <= 0 || sourceHeight <= 0) return null;

  const { x1, x2, y1, y2 } = boundingBox;

  if (![x1, x2, y1, y2].every(isFiniteNumber)) return null;

  const left = clampUnit(Math.min(x1, x2));
  const right = clampUnit(Math.max(x1, x2));
  const top = clampUnit(Math.min(y1, y2));
  const bottom = clampUnit(Math.max(y1, y2));

  if (right <= left || bottom <= top) return null;

  const padding = isFiniteNumber(pad) && pad >= 0 ? pad : DEFAULT_CROP_PAD;

  const boxWidth = (right - left) * sourceWidth;
  const boxHeight = (bottom - top) * sourceHeight;
  const centerX = ((left + right) / 2) * sourceWidth;
  const centerY = ((top + bottom) / 2) * sourceHeight;

  const paddedWidth = boxWidth * (1 + 2 * padding);
  const paddedHeight = boxHeight * (1 + 2 * padding);

  // Integer-ise before clamping the offsets. A region one pixel past the source edge is a hard
  // 500 from Fastly, and clamping x against a fractional width can round straight into it.
  const width = Math.floor(
    Math.min(Math.max(paddedWidth, paddedHeight * aspect), sourceWidth, sourceHeight * aspect)
  );
  const height = Math.floor(width / aspect);

  if (width <= 0 || height <= 0) return null;

  return {
    width,
    height,
    x: Math.min(Math.max(Math.round(centerX - width / 2), 0), sourceWidth - width),
    y: Math.min(Math.max(Math.round(centerY - height / 2), 0), sourceHeight - height)
  };
};

/** Fastly's pixel-crop form: comma-separated size (never `WxH`) with labelled offsets. */
export const formatCropParam = ({ width, height, x, y }: CropBox) =>
  `${width},${height},x${x},y${y}`;
